<script setup lang="ts">
/**
 * Карточка сводки раздела DevOps — макет 90:759: цветная точка, оверлайн-
 * подпись, крупное число и пояснение рядом с ним.
 *
 * Не AppStatCard из дизайн-системы: у того подпись сверху и дельта справа, а
 * здесь пояснение стоит в строку с числом и несёт другой смысл — не «сколько
 * изменилось», а «из чего это число».
 */
defineProps<{
  label: string
  value: string | number
  hint?: string
  /** Цвет точки: он же кодирует смысл — норма, ожидание, тревога */
  tone?: 'success' | 'info' | 'warning' | 'danger' | 'primary'
}>()
</script>

<template>
  <div class="metric">
    <div class="metric__head">
      <span class="metric__dot" :class="`metric__dot--${tone ?? 'primary'}`" />
      <span class="metric__label">{{ label }}</span>
    </div>
    <div class="metric__body">
      <span class="metric__value">{{ value }}</span>
      <span v-if="hint" class="metric__hint">{{ hint }}</span>
    </div>
  </div>
</template>

<style scoped>
.metric {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
}

.metric__head {
  display: flex;
  align-items: center;
  gap: 6px;
}

.metric__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.metric__dot--success {
  background: var(--color-success);
}

.metric__dot--info {
  background: var(--color-info);
}

.metric__dot--warning {
  background: var(--color-warning);
}

.metric__dot--danger {
  background: var(--color-danger);
}

.metric__dot--primary {
  background: var(--color-primary);
}

.metric__label {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.metric__body {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.metric__value {
  color: var(--color-text-primary);
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-semibold);
}

.metric__hint {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
